import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CarouselItemGeneralAdd from "./CarouselItemGeneralAdd";
import CarouselItemFirstAdd from "./CarouselItemFirstAdd";

export default function InfiniteCarousel({ slides }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlayingCarousel, setIsPlayingCarousel] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  // Función para ir al siguiente slide
  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [slides.length, isTransitioning]);

  // Función para ir al slide anterior
  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
    setTimeout(() => setIsTransitioning(false), 300);
  }, [slides.length, isTransitioning]);

  // Auto-play del carousel
  useEffect(() => {
    if (!isPlayingCarousel) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 4000);
    return () => clearInterval(interval);
  }, [isPlayingCarousel, nextSlide]);

  // Función para ir a un slide específico
  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Toggle play/pause
  const togglePlayPauseCarousel = () => {
    setIsPlayingCarousel(!isPlayingCarousel);
  };

  // Soporte touch/swipe para mobile
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const distance = touchStartX - touchEndX;
    if (Math.abs(distance) > 50) {
      if (distance > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <>
      <div className="relative w-full mx-auto bg-transparent shadow-2xl overflow-hidden">
        {/* Contenedor principal del carousel */}
        <div
          className="relative h-44 sm:h-72 overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Slides */}
          <div
            className="flex transition-transform duration-300 ease-in-out h-full"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {slides.map((slide, index) => {
              switch (slide.adsRenderComponent) {
                case "CarouselItemGeneralAdd":
                  return <CarouselItemGeneralAdd key={index} item={slide} />;
                case "CarouselItemFirstAdd":
                  return <CarouselItemFirstAdd key={index} item={slide} />;
                default:
                  return <CarouselItemGeneralAdd key={index} item={slide} />;
              }
            })}
          </div>
          {/* Controles de navegación */}
          <button
            onClick={() => {
              prevSlide();
              isPlayingCarousel === false && togglePlayPauseCarousel();
            }}
            disabled={isTransitioning}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white opacity-5 hover:opacity-50 backdrop-blur-sm text-aw-bluedark p-3 rounded-full transition-all duration-200 hover:scale-110 disabled:opacity-50"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-left" size="lg" />
          </button>

          <button
            onClick={() => {
              nextSlide();
              isPlayingCarousel === false && togglePlayPauseCarousel();
            }}
            disabled={isTransitioning}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white opacity-5 hover:opacity-50 backdrop-blur-sm text-aw-bluedark p-3 rounded-full transition-all duration-200 hover:scale-110 disabled:opacity-50"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-right" size="lg" />
          </button>

          {/* Control de play/pause */}
          <button
            onClick={togglePlayPauseCarousel}
            className="absolute top-4 right-4 bg-white opacity-5 hover:opacity-50 backdrop-blur-sm text-aw-bluedark p-2 rounded-full transition-all duration-200 hover:scale-110"
          >
            {isPlayingCarousel ? (
              <FontAwesomeIcon icon="fa-solid fa-pause" size={20} />
            ) : (
              <FontAwesomeIcon icon="fa-solid fa-play" size={20} />
            )}
          </button>
        </div>
        {/* Estilos CSS personalizados */}
        <style jsx="true">{`
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slide-up {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in {
            animation: fade-in 0.6s ease-out;
          }

          .animate-slide-up {
            animation: slide-up 0.8s ease-out 0.2s both;
          }
        `}</style>
      </div>

      {/* Indicadores de navegación */}
      <div className="flex justify-center items-center space-x-2 p-0 bg-aw-bluedark-30">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            className={`w-1.5 h-0 rounded-full transition-all duration-200 hover:scale-125 ${
              index === currentIndex
                ? "bg-blue-500 scale-125"
                : "bg-gray-300 hover:bg-gray-400"
            }`}
          />
        ))}
      </div>
    </>
  );
}
